import React from 'react';
import styled, { css } from 'styled-components';
import {
  IconMenu,
  Image,
  IconUserAltCircle,
  Type,
  IconMoreAndroid,
  ButtonIcon,
} from '@spotify-internal/encore-creator-web';
import {
  white,
  spacer20,
  gray7,
  body2FontSize,
  screenSmMax,
  screenSm,
  gray20,
  spacer12,
  gray15,
} from '@spotify-internal/encore-foundation';
import { useMe } from '../../../hooks/useMe';
import { useIntl, defineMessage } from 'react-intl';

type Props = {
  toggleUserFlyout: () => void;
  navLinks: React.ReactNode;
  navFlyoutOpen: boolean;
  toggleMobileNavigation: () => void;
};

export function Header(props: Props) {
  const me = useMe()!;
  const intl = useIntl();
  const msg = defineMessage({
    id: 'navigation.toggle',
    desciption: 'Text telling user to switch between side navigation being visible or not',
    defaultMessage: 'Toggle Navigation',
  });

  return (
    <HeaderContainer>
      <FlyoutButtonContainer data-testid="user-nav-open" onClick={props.toggleUserFlyout}>
        <IconMoreAndroid size={24} />
        <AvatarWrapper>
          {Boolean(me.avatarUrl) ? (
            <Image
              src={me.avatarUrl}
              imageHeight="36px"
              imageWidth="36px"
              alt={me.displayName}
              crop
              circle
              data-testid="nav-avatar"
            />
          ) : (
            <IconUserAltCircle
              iconSize={36}
              aria-label={me.displayName}
              data-testid="nav-avatar-fallback"
            />
          )}
        </AvatarWrapper>
        <Type as="p" variant={Type.body2} weight={Type.bold} style={{ paddingBottom: 0 }}>
          {me.displayName}
        </Type>
      </FlyoutButtonContainer>
      <Nav data-testid="top-nav">{props.navLinks}</Nav>
      <MobileNavButton
        aria-label={intl.formatMessage(msg)}
        aria-expanded={String(props.navFlyoutOpen)}
        onClick={props.toggleMobileNavigation}
      >
        <IconMenu color={white} />
      </MobileNavButton>
    </HeaderContainer>
  );
}

const HeaderPadding = css`
  padding: ${spacer20} ${spacer12};

  @media (min-width: ${screenSmMax}) {
    padding: 31px ${spacer20};
  }
`;

const HeaderContainer = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  ${HeaderPadding}
  background-color: ${gray7};
  color: ${white};
  font-size: ${body2FontSize};

  @media (min-width: ${screenSmMax}) {
    justify-content: center;
  }
`;

const FlyoutButtonContainer = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  height: 100%;
  border: 0;
  background-color: ${gray7};
  color: ${white};
  cursor: pointer;

  &:hover {
    background-color: ${gray15};
  }

  &:focus {
    background-color: ${gray20};
  }

  padding-right: ${spacer12};

  @media (min-width: ${screenSmMax}) {
    padding-right: ${spacer20};
  }
`;

const AvatarWrapper = styled.div`
  margin-left: 4px;
  margin-right: 12px;
  display: flex;
`;

const Nav = styled.nav`
  display: none;

  a:not(:last-child) {
    margin-right: 26px;
  }

  @media (min-width: ${screenSm}) {
    display: block;
  }
`;

const MobileNavButton = styled(ButtonIcon)`
  @media (min-width: ${screenSm}) {
    display: none;
  }
`;
